import Loading from '@/components/Loading';
import { _isNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { Measurement } from '@/model/Measurement.model';
import type { Study } from '@/model/Study.model';
import { errorToast, successToast } from '@/helpers';
import { useFetchCollection } from '@/support/Hooks/fetch';
import { modalAction } from '@/utils/modal';
import { type FC, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Measurements from './Measurements';
import type { ID } from '@/model/Common.model.ts';
import type { MeasurementEvent } from '@/components/Subjects/Show/Measurements/MeasurementWorkflow.model.tsx';

interface MeasurementsSidebarProps {
  study: Study;
  animal: Animal;
  measurementsUpdated: () => Promise<void>;
}

const MeasurementsSidebar: FC<MeasurementsSidebarProps> = ({ study, animal, measurementsUpdated }) => {
  const dispatch = useDispatch();
  const { closeModal } = modalAction(dispatch);
  const { collection: measurements, collectionLoading: measurementsLoading } = useFetchCollection<Measurement>({
    collectionType: 'measurements',
    params: { id: animal.id },
  });

  const alertVariableIds = useMemo(
    (): Set<ID> =>
      (animal.alerts ?? []).reduce<Set<ID>>((acc, alert) => {
        if (_isNil(alert.resolved_at)) {
          acc.add(alert.variable_id);
        }
        return acc;
      }, new Set<ID>()),
    [animal.alerts]
  );

  const handleMeasurementEvent = async (event: MeasurementEvent) => {
    if (event.type === 'deleted') {
      if (event.success) {
        await measurementsUpdated();
        successToast('Successfully deleted measurement');
      } else {
        errorToast('There was a problem deleting the measurement');
      }
      return;
    }
    await measurementsUpdated();
    closeModal();
    successToast('Success.');
  };

  return (
    <div
      style={{ width: 400 }}
      className="bl b--moon-gray bg-white absolute right-0 top-0 z-5 h-100 overflow-auto"
      data-testid="animals-measurements-sidebar"
    >
      {measurementsLoading ? (
        <Loading txt="Fetching measurements..." />
      ) : (
        <Measurements
          subject={animal}
          study={study}
          measurements={measurements ?? []}
          onMeasurementSubmit={handleMeasurementEvent}
          fromWorkflow={false}
          alertVariableIds={alertVariableIds}
        />
      )}
    </div>
  );
};

export default MeasurementsSidebar;
